import { ArrowRight, ChevronLeft, ChevronRight, X, Sparkles, TrendingUp, Zap, Target, BookOpen, Users, Award, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect, useCallback, forwardRef, type TouchEvent } from "react";
import newsCard1 from "@/assets/news-iichnitsa.jpg";
import newsCard2 from "@/assets/news-armenia.jpg";
import newsMethodology from "@/assets/news-methodology.jpg";
import newsCard3 from "@/assets/news-patent.jpg";
import newsCard4 from "@/assets/news-t1.jpg";
import newsNeurokey from "@/assets/news-neurokey.jpg";
import newsArmeniaDetail from "@/assets/news-armenia-detail.jpg";
import newsNeurokeyDetail from "@/assets/news-neurokey-detail.png";
import newsT1Detail from "@/assets/news-t1-detail.jpg";
import news8march from "@/assets/news-8march.jpg";
import stanfordCertificate from "@/assets/stanford-certificate.jpg";
import newsIichnicaShow from "@/assets/iichnica-show.jpg";
import newsPersonalAI from "@/assets/news-personal-ai-intensive.jpg";
import newsWmtKidsNew from "@/assets/news-wmt-kids-new.jpg";
import newsWmtKidsGraduation from "@/assets/news-wmt-kids-graduation.jpg";
import newsNeurokeyUpdate2 from "@/assets/news-neurokey-update2.jpg";
import newsLeadersXO from "@/assets/news/leaders-xo-2026.jpg";
import leadersXoGallery1 from "@/assets/news/leaders-xo-gallery-1.jpg";
import leadersXoGallery2 from "@/assets/news/leaders-xo-gallery-2.jpg";
import leadersXoGallery3 from "@/assets/news/leaders-xo-gallery-3.jpg";
import leadersXoGallery4 from "@/assets/news/leaders-xo-gallery-4.jpg";
import leadersXoGallery5 from "@/assets/news/leaders-xo-gallery-5.jpg";
import newsIdBankCover from "@/assets/news/idbank-cover.jpg";
import idBankGallery1 from "@/assets/news/idbank-gallery-1.jpg";
import idBankGallery2 from "@/assets/news/idbank-gallery-2.jpg";
import idBankGallery3 from "@/assets/news/idbank-gallery-3.jpg";

const leadersXoGalleryImages = [leadersXoGallery1, leadersXoGallery2, leadersXoGallery3, leadersXoGallery4, leadersXoGallery5];
const idBankGalleryImages = [idBankGallery1, idBankGallery2, idBankGallery3];

const GalleryCarousel = ({ images, label }: { images: string[]; label: string }) => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const len = images.length;
  const thumbStripRef = useRef<HTMLDivElement>(null);

  const go = useCallback((dir: number) => {
    setDirection(dir);
    setCurrent((c) => ((c + dir) % len + len) % len);
  }, [len]);

  const goTo = useCallback((idx: number) => {
    setDirection(idx > current ? 1 : -1);
    setCurrent(idx);
  }, [current]);

  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const handleTouchStart = (e: TouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const handleTouchEnd = (e: TouchEvent) => {
    if (!touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
      go(dx < 0 ? 1 : -1);
    }
    touchStart.current = null;
  };

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen, go]);

  useEffect(() => {
    if (isPaused || fullscreen) return;
    const id = setInterval(() => go(1), 5000);
    return () => clearInterval(id);
  }, [isPaused, fullscreen, go]);

  useEffect(() => {
    if (fullscreen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [fullscreen]);

  useEffect(() => {
    const strip = thumbStripRef.current;
    if (!strip) return;
    const active = strip.querySelector<HTMLElement>(`[data-thumb-idx="${current}"]`);
    if (active) {
      const sLeft = strip.scrollLeft;
      const sRight = sLeft + strip.clientWidth;
      const aLeft = active.offsetLeft;
      const aRight = aLeft + active.offsetWidth;
      if (aLeft < sLeft + 20 || aRight > sRight - 20) {
        strip.scrollTo({ left: aLeft - strip.clientWidth / 2 + active.offsetWidth / 2, behavior: "smooth" });
      }
    }
  }, [current]);

  const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? 60 : -60, opacity: 0, scale: 1.02 }),
    center: { x: 0, opacity: 1, scale: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? -60 : 60, opacity: 0, scale: 0.98 }),
  };

  return (
    <>
      <div className="mb-8" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
        <div
          className="relative w-full aspect-[16/10] sm:aspect-[16/9] rounded-2xl overflow-hidden shadow-[0_20px_60px_-20px_hsl(var(--accent)/0.35)] bg-muted group cursor-zoom-in"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onClick={() => setFullscreen(true)}
        >
          <AnimatePresence mode="wait" custom={direction}>
            <motion.img
              key={current}
              src={images[current]}
              alt={`${label} фото ${current + 1}`}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
              className="w-full h-full object-cover absolute inset-0"
            />
          </AnimatePresence>

          <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/40 to-transparent pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />

          <div className="absolute top-4 right-4 z-10 px-3 py-1.5 rounded-full bg-background/90 backdrop-blur-md border border-border/50 shadow-sm">
            <span className="text-[11px] font-heading text-foreground tabular-nums">
              <span className="text-accent font-semibold">{String(current + 1).padStart(2, "0")}</span>
              <span className="text-muted-foreground"> / {String(len).padStart(2, "0")}</span>
            </span>
          </div>

          <button onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-background/90 backdrop-blur-md border border-border/50 flex items-center justify-center shadow-md hover:bg-accent hover:text-accent-foreground hover:scale-105 active:scale-95 transition-all opacity-100 sm:opacity-0 sm:group-hover:opacity-100" aria-label="Предыдущее фото">
            <ChevronLeft size={18} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-background/90 backdrop-blur-md border border-border/50 flex items-center justify-center shadow-md hover:bg-accent hover:text-accent-foreground hover:scale-105 active:scale-95 transition-all opacity-100 sm:opacity-0 sm:group-hover:opacity-100" aria-label="Следующее фото">
            <ChevronRight size={18} />
          </button>

          <button onClick={(e) => { e.stopPropagation(); setFullscreen(true); }} className="absolute bottom-4 right-4 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/90 backdrop-blur-md border border-border/50 shadow-sm hover:bg-accent hover:text-accent-foreground transition-all" aria-label="Открыть на весь экран">
            <Maximize2 size={13} />
            <span className="text-[11px] font-heading uppercase tracking-wider">Открыть</span>
          </button>

          <div className="absolute bottom-0 left-0 right-0 h-1 bg-background/30 z-10">
            <motion.div key={current + (isPaused ? "p" : "r")} initial={{ width: "0%" }} animate={{ width: isPaused ? "0%" : "100%" }} transition={{ duration: isPaused ? 0 : 5, ease: "linear" }} className="h-full bg-accent" />
          </div>
        </div>

        <div className="mt-4 relative">
          <div ref={thumbStripRef} className="flex gap-2 overflow-x-auto pb-2 scroll-smooth snap-x snap-mandatory touch-pan-x overscroll-x-contain" style={{ scrollbarWidth: "none" }}>
            {images.map((src, i) => (
              <button key={i} data-thumb-idx={i} onClick={() => goTo(i)} className={`relative shrink-0 snap-start rounded-lg overflow-hidden transition-all duration-300 ${i === current ? "w-20 h-16 sm:w-24 sm:h-20 ring-2 ring-accent ring-offset-2 ring-offset-background scale-100" : "w-16 h-14 sm:w-20 sm:h-16 opacity-60 hover:opacity-100 hover:scale-105"}`} aria-label={`Перейти к фото ${i + 1}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
                {i !== current && <div className="absolute inset-0 bg-background/20" />}
              </button>
            ))}
          </div>
          <style>{`.scroll-smooth::-webkit-scrollbar{display:none}`}</style>
        </div>
      </div>

      <AnimatePresence>
        {fullscreen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/95 backdrop-blur-md flex flex-col" style={{ zIndex: 10000 }} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 z-20">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                <span className="text-[11px] font-heading uppercase tracking-widest text-white">{label}</span>
                <span className="text-white/50 text-[11px] font-heading tabular-nums ml-1">{String(current + 1).padStart(2, "0")} / {String(len).padStart(2, "0")}</span>
              </div>
              <button onClick={() => setFullscreen(false)} className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-all active:scale-95" aria-label="Закрыть">
                <X size={20} className="text-white" />
              </button>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 sm:px-16 overflow-hidden relative">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.img key={current} src={images[current]} alt={`${label} фото ${current + 1}`} custom={direction} variants={{ enter: (dir: number) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }), center: { x: 0, opacity: 1 }, exit: (dir: number) => ({ x: dir > 0 ? -80 : 80, opacity: 0 }) }} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
              </AnimatePresence>

              <button onClick={() => go(-1)} className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 items-center justify-center transition-all active:scale-95" aria-label="Предыдущее фото">
                <ChevronLeft size={22} className="text-white" />
              </button>
              <button onClick={() => go(1)} className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 items-center justify-center transition-all active:scale-95" aria-label="Следующее фото">
                <ChevronRight size={22} className="text-white" />
              </button>
            </div>

            <div className="px-4 sm:px-6 pb-5 pt-3">
              <div className="flex gap-2 overflow-x-auto justify-start sm:justify-center touch-pan-x overscroll-x-contain" style={{ scrollbarWidth: "none" }}>
                {images.map((src, i) => (
                  <button key={i} onClick={() => goTo(i)} className={`relative shrink-0 rounded-md overflow-hidden transition-all duration-300 ${i === current ? "w-16 h-12 sm:w-20 sm:h-14 ring-2 ring-accent" : "w-14 h-10 sm:w-16 sm:h-12 opacity-50 hover:opacity-100"}`} aria-label={`Перейти к фото ${i + 1}`}>
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const news = [
  {
    title: "Стратсессия по AI-трансформации для ID Bank в Ереване",
    cardTitle: "Стратсессия по AI-трансформации для ID Bank",
    titleJsx: <>Стратсессия по <span className="text-accent">AI-трансформации</span> для ID Bank в Ереване</>,
    img: newsIdBankCover,
    summary: "6 мая команда WMT AI работает в Ереване с ID Bank: проводим однодневную закрытую стратсессию по AI-трансформации для всего руководящего слоя банка.\n\nФормат — интенсивный рабочий день в Armenia Marriott Hotel Yerevan: разбираем, как искусственный интеллект перестраивает банковскую модель, какие процессы дают максимальный эффект от внедрения и как выстроить системный путь трансформации, а не точечные пилоты.\n\nИгорь Никитин, основатель и CEO WMT AI, ведёт сессию в формате прямого диалога с топ-командой: реальные кейсы, авторская методология AI-трансформации, разбор конкретных задач банка и фиксация шагов на ближайший горизонт. Это продолжение нашей работы с финансовым сектором Армении и ещё один шаг в развитии ИИ-повестки в регионе.",
    takeaways: [
      "Однодневная закрытая стратсессия для C-level ID Bank",
      "Площадка — Armenia Marriott Hotel Yerevan",
      "Авторская методология AI-трансформации в банковской модели",
      "Прямой диалог с руководящим слоем банка",
      "Развитие ИИ-повестки в финансовом секторе Армении",
    ],
    detailIcons: [TrendingUp, Users, Target],
    galleryImages: idBankGalleryImages,
  },
  {
    title: "Личный ИИ — 2 дня обучения работы с ИИ: 16 часов офлайн-практики для C-level",
    cardTitle: "Личный ИИ — 2 дня обучения работы с ИИ",
    titleJsx: <><span className="text-accent">Личный ИИ</span> — 2 дня обучения работы с ИИ: 16 часов офлайн-практики для C-level</>,
    img: newsPersonalAI,
    link: "https://t.me/lichniyii",
    linkText: "Программа и запись",
    summary: "16-часовое офлайн обучение на 2 дня для руководителей. В рамках 16 часов разбираем управленческие задачи и решения, финансы, HR, договоры, маркетинг — реальные кейсы из бизнеса, не теорию. Отдельный фокус — личная эффективность и работа с ИИ в деньгах и времени.\n\nКаждый участник работает со своими задачами: документы, отчёты, решения, процессы. Финальный акцент — эффективная работа с ИИ на ваших задачах.\n\nЧто вы получаете:\n— карта ИИ-инструментов под свои задачи\n— 2 дня практики на реальных задачах\n— настроенный личный ассистент\n— промпты для управленческих решений\n— шаблоны для HR, финансов и договоров\n— система работы с документами и аналитикой\n— рабочий workflow в n8n для мониторинга конкурентов\n— персональная ИИ-операционка\n— сертификат о прохождении обучения\n\nПосле интенсива у вас не знания про ИИ, а работающие инструменты под бизнес.",
    takeaways: [
      "16 часов офлайн-практики за 2 дня · формат C-level",
      "Личный разбор задач от Игоря Никитина",
      "Карта ИИ и настроенный личный ассистент",
      "Шаблоны для HR, финансов, договоров и workflow",
      "Персональный сертификат о прохождении обучения",
    ],
    detailIcons: [Sparkles, Users, Target],
    extraLinks: [{ label: "Канал Игоря", url: "https://t.me/Nikitinwmt" }],
  },
  {
    title: "Стартует 3 поток WMT Kids — старт 2 мая",
    cardTitle: "Старт 3 потока WMT Kids",
    titleJsx: <>Старт 3 потока <span className="text-accent">WMT Kids</span></>,
    img: newsWmtKidsNew,
    link: "https://kids.wmtunnel.ru",
    linkText: "Анкета для записи",
    summary: "Мы запускаем третий поток WMT Kids — программу, где дети не просто знакомятся с ИИ, а учатся мыслить и создавать собственные проекты с его помощью.\n\nОффлайн (офис на Остоженке), суббота 2 мая:\n— 13:00 — младшая группа (10–13 лет)\n— 14:30 — старшая группа (14–17 лет)\n\nОнлайн (та же программа):\n— 7 мая, четверг, 17:30 МСК — младшая группа\n— 8 мая, пятница, 17:30 МСК — старшая группа",
    takeaways: [
      "Форматы: оффлайн на офисе и онлайн",
      "Две группы: 10–13 and 14–17 лет",
      "Форматы: оффлайн на Остоженке и онлайн",
    ],
    detailIcons: [Sparkles, Users, Target],
    extraLinks: [
      { label: "Менеджер обучения", url: "https://t.me/wmtkidsai" },
      { label: "Новости проекта", url: "https://t.me/wmtkids" },
    ],
  },
  {
    title: "Первый поток WMT Kids: 8 выпускников, защита проектов и сертификаты",
    cardTitle: "Первый поток WMT Kids завершён",
    titleJsx: <>Первый поток <span className="text-accent">WMT Kids</span>: защита проектов и сертификаты</>,
    img: newsWmtKidsGraduation,
    link: "https://t.me/wmtkidsai",
    linkText: "Записаться на новый поток",
    summary: "Первый поток WMT Kids завершён! На финальной встрече участники вместе с родителями показали, чему научились за время обучения, защитили свои проекты и получили сертификаты.\n\nДети собрали игру в духе тамагочи, гид по Москве, ИИ-репетитора для школьных тем и агента, который создаёт презентации!\n\nПосле защит и получение дипломов семьи пообщались, обменялись впечатлениями и провели время вместе.",
    takeaways: [
      "8 выпускников защитили проекты",
      "Каждый участник получил сертификат",
      "Готовые ИИ-проекты",
      "Открыта запись на новый поток",
    ],
    detailIcons: [Award, Users, Sparkles],
    extraLinks: [{ label: "Новости проекта", url: "https://t.me/wmtkids" }],
  },
  {
    title: "WMT AI на «Лидеры XO» 2026: собственный стенд и выступление Игоря Никитина",
    cardTitle: "WMT AI на «Лидеры XO» 2026",
    titleJsx: <>WMT AI на <span className="text-accent">«Лидеры XO» 2026</span>: собственный стенд и выступление Игоря Никитина</>,
    img: newsLeadersXO,
    link: "https://adizes.ru/leadersxo2026",
    linkText: "Сайт мероприятия",
    summary: "«Лидеры XO» — ежегодная флагманская конференция Института Адизеса для собственников и первых лиц бизнеса. WMT AI представлен собственным стендом, посвящённым AI-трансформации: показываем методологию, продукты и реальные кейсы внедрения.\n\n24 числа на сцене выступает Игорь Никитин — основатель WMT AI. Тема — как 5% руководителей переходят в статус «Лидеры» через системную работу с искусственным интеллектом. Это редкая площадка, где ИИ-повестка обсуждается на уровне принимающих решения, а не исполнителей.",
    takeaways: [
      "Собственный стенд WMT AI на главной площадке для собственников бизнеса",
      "Выступление Игоря Никитина 24 числа",
      "AI-трансформация в фокусе разговора с C-level аудиторией",
      "Прямой диалог с первыми лицами бизнеса о внедрении ИИ",
    ],
    detailIcons: [Award, Users, Sparkles],
    galleryImages: leadersXoGalleryImages,
  },
  {
    title: "Обновление Нейроключа: групповые чаты, базы знаний и светлая тема",
    cardTitle: "Новое обновление Нейроключа",
    titleJsx: <>Новое обновление <span className="text-accent">Нейроключа</span></>,
    img: newsNeurokeyUpdate2,
    link: "https://wmt-ai.ru/neiroklyuch/",
    linkText: "Попробовать Нейроключ",
    summary: "Групповые чаты (Песочница) стали мощнее: теперь прямо в чате можно работать с документами, изображениями, веб-поиском и генерацией картинок. Команда обсуждает задачи вместе с ИИ в едином потоке — без пересылки промптов.\n\nБазы знаний теперь доступны всем пользователям, а не только администраторам. При выборе модели появились индикаторы мощности и стоимости. Мобильный интерфейс доработан: модель и быстрые действия — в хедере. Светлая тема переработана в холодных тонах. Исправлено 30 багов в групповых и личных чатах.",
    takeaways: [
      "Песочница: усиленная работа в групповом чате",
      "Базы знаний доступны всем пользователям",
      "Индикаторы мощности и стоимости моделей",
      "Обновлённый мобильный интерфейс и светлая тема",
      "30 исправленных багов",
    ],
    detailIcons: [Sparkles, TrendingUp, Zap],
  },
  {
    title: "ИИЧНИЦА SHOW: 300+ топ-менеджеров, 10+ представителей СМИ",
    cardTitle: "ИИЧНИЦА SHOW: Как это было",
    titleJsx: <><span className="text-accent">ИИЧНИЦА SHOW</span>: 300+ топ-менеджеров, 10+ представителей СМИ</>,
    img: newsIichnicaShow,
    link: "https://ost-iichnica.vercel.app/",
    linkText: "Полный отчёт и материалы",
    summary: "300+ топ-менеджеров из Ростелеком, Сбер, Т1, Альфа Банк, Газпром, Билайн, Иннотех, Росатом, Р1, Рольф и др. 10+ представителей СМИ: РБК, Коммерсант, Известия, CNews и др.  Скоро отдельные выпуски по каждому спикеру.",
    takeaways: [
      "300+ участников: C-level из крупнейших компаний",
      "10+ СМИ: РБК, Коммерсант, Известия, CNews и др.",
      "Саммари всех выступлений и запись эфира на сайте",
    ],
    detailIcons: [Users, TrendingUp, Target],
  },
  {
    title: "Сотрудничество с Центральным Банком Армении: масштабная AI-трансформация",
    cardTitle: "Сотрудничество с Центральным Банком Армении",
    titleJsx: <>Сотрудничество с Центральным Банком Армении: <span className="text-accent">масштабная AI-трансформация</span></>,
    img: newsCard2,
    detailImg: newsArmeniaDetail,
    summary: "WMT AI заключил стратегическое партнёрство с Центральным Банком Армении для AI-трансформации работы компании. Налажены дружеские отношения между Россией и Арменией, простроены планы по взаимодействию на весь год.",
    takeaways: [
      "Стратегическое партнёрство с ЦБА",
      "AI-трансформация рабочих процессов ЦБА",
      "Долгосрочные планы сотрудничества на год",
    ],
    detailIcons: [TrendingUp, Zap, Target],
    extraLinks: [{ label: "Отзыв ЦБА", url: "https://www.linkedin.com/feed/update/urn:li:activity:7434170043686617088/" }],
  },
  {
    title: "Собственная методология AI-трансформации",
    cardTitle: "Собственная методология AI-трансформации",
    titleJsx: <>Собственная методология <span className="text-accent">AI-трансформации</span></>,
    img: newsMethodology,
    certificateImg: stanfordCertificate,
    summary: "На основе этой методологии, одобренную в Shanghai Jiao Tong University, мы проводим корпоративную программу обучения AI-трансформации бизнеса.",
    takeaways: [
      "Авторская методология ИИ Transformation Architecture",
      "Реализация в рамках Shanghai Jiao Tong University",
      "Корпоративная программа обучения",
    ],
    detailIcons: [Sparkles, BookOpen, Zap],
  },
  {
    title: "WMT AI анонсировало новую флагманскую ИИчницу 31 марта",
    cardTitle: "Анонс новой флагманской ИИчницы 31 марта",
    titleJsx: <>WMT AI анонсировало <span className="text-accent">новую флагманскую ИИчницу</span> 31 марта</>,
    img: newsCard1,
    link: "https://wmtaichnica.ru/?utm_source=artur&utm_medium=referral&utm_campaign=wmtaichnica&utm_content=personal",
    linkText: "Полная информация и регистрация",
    summary: "Компания WMT AI представит новый формат флагманского мероприятия «ИИчница» — открытую площадку, где эксперты, предприниматели и разработчики обсудят AI-трансформацию не только бизнеса, но и всего мира. Мероприятие пройдёт в Москве. Живое общение, а не \"сухие\" доклады.",
    takeaways: [
      "Спикеры: Игорь Никитин, Гор Нахапетян, Андрей Дороничев, Сергей Марков, Равиль Шакиров, Алексей Савватеев",
      "Участие уникального детского образовательного ИИ проекта WMT Kids",
      "Открытая площадка для экспертов, предпринимателей и разработчиков",
    ],
    detailIcons: [Users, Sparkles, Target],
  },
  {
    title: "Обновление Нейроключа: новый UI, статистика и исправления",
    cardTitle: "Обновление Нейроключа: новый UI, статистика и исправления",
    titleJsx: <>Обновление <span className="text-accent">Нейроключа</span>: новый UI, статистика и исправления</>,
    img: newsNeurokey,
    detailImg: newsNeurokeyDetail,
    link: "https://wmt-ai.ru/neiroklyuch/",
    linkText: "Протестировать нейроключ",
    summary: "Развёрнутое обновление по Нейроключу: улучшен UI в диалогах, добавлена статистика с временем, проведённым в Нейроключе, и количеством запросов по каждой модели. Доработаны карточки с описанием моделей, исправлены критические баги с мобильной версией. Улучшена обработка документов и изображений, исправлены проблемы с отображением чатов и переходом между ними.",
    takeaways: [
      "Улучшен UI в диалогах",
      "Добавлена статистика использования",
      "Доработаны карточки с описанием моделей",
      "Исправлены критические баги мобильной версии",
      "Улучшена обработка документов и картинок",
      "Исправлены проблемы и баги",
    ],
    detailIcons: [Sparkles, TrendingUp, Zap],
  },
  {
    title: "Бесплатный ИИ-марафон для девушек в честь 8 марта!",
    cardTitle: "Бесплатный ИИ-марафон для девушек в честь 8 марта!",
    titleJsx: <>Бесплатный <span className="text-accent">ИИ-марафон</span> для девушек в честь 8 марта!</>,
    img: news8march,
    link: "https://dialogue.wmtunnel.ru",
    linkText: "Перейти к марафону",
    summary: "К 8 марта мы подготовили подарок для прекрасных клиентов и партнёров. Мы даём доступ к обучающему марафону, адаптированному специально для девушек. Короткий и простой формат: уроки в записи, примеры, практические задания и полезные материалы — всё на нашей платформе. Суть марафона — показать, как ИИ реально помогает в жизни.",
    takeaways: [
      "Помощь в бытовых задачах с помощью ИИ",
      "Планирование и организация через ИИ-инструменты",
      "Повышение эффективности дома и в работе",
      "Быстрый и понятный вход в ИИ для начинающих",
    ],
    detailIcons: [BookOpen, Users, Target],
  },
];

const CARD_GAP = 24;

const NewsSection = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [certExpanded, setCertExpanded] = useState(false);

  useEffect(() => {
    if (expandedIndex !== null) {
      setCertExpanded(false);
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [expandedIndex]);

  const scrollToIndex = useCallback((index: number) => {
    const el = scrollRef.current;
    const card = el?.querySelector(`[data-card-index="${index}"]`) as HTMLElement;
    if (el && card) {
      const scrollPos = card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2;
      el.scrollTo({ left: scrollPos, behavior: "smooth" });
    }
  }, []);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);

    const cards = el.querySelectorAll("[data-card-index]");
    const containerCenter = el.scrollLeft + el.clientWidth / 2;
    let closest = 0;
    let closestDist = Infinity;
    cards.forEach((card, i) => {
      const rect = card as HTMLElement;
      const cardCenter = rect.offsetLeft + rect.offsetWidth / 2;
      const dist = Math.abs(containerCenter - cardCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closest = i;
      }
    });
    setActiveIndex(closest);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll, { passive: true });
    window.addEventListener("resize", checkScroll);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [checkScroll]);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const firstCard = el.querySelector("[data-card-index]") as HTMLElement;
    const amount = firstCard ? firstCard.offsetWidth + CARD_GAP : 320;
    el.scrollBy({ left: dir === "right" ? amount : -amount, behavior: "smooth" });
  };

  return (
    <section id="news" className="py-28 lg:py-36 overflow-hidden">
      <div className="container mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 lg:mb-16"
        >
          <SectionHeading>Новости и <span className="text-accent">инсайты</span></SectionHeading>
        </motion.div>
      </div>

      {/* Carousel */}
      <div className="relative" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
        <button
          onClick={() => scroll("left")}
          disabled={!canScrollLeft}
          className="absolute left-3 sm:left-6 lg:left-10 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full border border-accent/40 bg-background/80 backdrop-blur-sm flex items-center justify-center text-accent hover:text-accent hover:border-accent transition-all disabled:opacity-0 disabled:pointer-events-none shadow-lg"
        >
          <ChevronLeft size={18} />
        </button>

        <button
          onClick={() => scroll("right")}
          disabled={!canScrollRight}
          className="absolute right-3 sm:right-6 lg:right-10 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full border border-accent/40 backdrop-blur-sm flex items-center justify-center text-accent hover:text-accent hover:border-accent transition-all disabled:opacity-0 disabled:pointer-events-none shadow-lg bg-background"
        >
          <ChevronRight size={18} className="text-accent" />
        </button>

        <div
          ref={scrollRef}
          className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4 touch-pan-x overscroll-x-contain"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            gap: `${CARD_GAP}px`,
            paddingLeft: "clamp(1.5rem, 5vw, 6rem)",
            paddingRight: "clamp(1.5rem, 5vw, 6rem)",
          }}
        >
          {news.map((item, i) => {
            const isActive = activeIndex === i;
            return (
              <div
                key={i}
                data-card-index={i}
                onClick={() => setExpandedIndex(i)}
                className="group relative flex-shrink-0 snap-center rounded-xl overflow-hidden cursor-pointer"
                style={{
                  width: "clamp(280px, 75vw, 380px)",
                  aspectRatio: "3 / 4",
                  transform: isActive ? "scale(1)" : "scale(0.95)",
                  opacity: 1,
                  transition: "transform 0.4s ease",
                }}
              >
                {/* Background Image */}
                <img
                  src={item.img}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />

                {/* Gradient Overlay */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: "linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.85) 100%)",
                  }}
                />

                {/* Thin border for depth */}
                <div className="absolute inset-0 rounded-2xl border border-border/20 pointer-events-none" />

                {/* Content */}
                <div className="relative z-10 flex flex-col justify-end h-full p-6 sm:p-7">
                  <h3 className="font-heading text-sm sm:text-base lg:text-lg leading-snug text-white">
                    {item.cardTitle}
                  </h3>
                  <button className="mt-4 self-start h-11 px-8 rounded-full bg-accent text-accent-foreground text-sm font-heading tracking-wide hover:bg-accent/90 transition-all duration-300">
                     Подробнее
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-6">
          {news.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                const el = scrollRef.current;
                const card = el?.querySelector(`[data-card-index="${i}"]`) as HTMLElement;
                if (el && card) {
                  const scrollPos = card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2;
                  el.scrollTo({ left: scrollPos, behavior: "smooth" });
                }
              }}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                activeIndex === i
                  ? "w-6 bg-accent"
                  : "w-1.5 bg-accent/30 hover:bg-accent/50"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Expanded Detail Modal — no images, creative text visualization */}
      <AnimatePresence>
        {expandedIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-end sm:items-center justify-center bg-background/80 backdrop-blur-md"
            style={{ zIndex: 9999 }}
            onClick={() => setExpandedIndex(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full sm:max-w-4xl h-[100dvh] sm:h-auto sm:max-h-[85vh] rounded-none sm:rounded-2xl overflow-hidden border-0 sm:border border-border/30 bg-card shadow-2xl flex flex-col"
              style={{ touchAction: "pan-y" }}
            >
              {/* Sticky header */}
              <div className="sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6 py-3 bg-card/95 backdrop-blur-md border-b border-border/30 flex-shrink-0">
                <span className="text-xs font-heading tracking-widest uppercase text-muted-foreground truncate mr-4">
                  {"\n"}
                </span>
                <button
                  onClick={() => setExpandedIndex(null)}
                  className="w-11 h-11 rounded-full bg-background/60 backdrop-blur-sm border border-border/30 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                  aria-label="Закрыть"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Scrollable content — no image, creative text */}
              <div className="flex-1 overflow-y-auto overscroll-contain" style={{ touchAction: "pan-y" }}>
                <div className="p-6 sm:p-8 lg:p-10">

                  <h3 className="font-heading text-2xl lg:text-3xl leading-snug text-foreground mb-5">
                    {news[expandedIndex].titleJsx || news[expandedIndex].title}
                  </h3>

                  {/* Creative accent divider */}
                  <div className="flex items-center gap-2 mb-7" aria-hidden>
                    <span className="h-[2px] w-10 rounded-full bg-gradient-to-r from-accent to-accent/60" />
                    <span className="w-1.5 h-1.5 rounded-full bg-accent shadow-[0_0_10px_hsl(var(--accent)/0.6)]" />
                    <span className="h-[2px] w-4 rounded-full bg-accent/40" />
                    <span className="w-1 h-1 rounded-full bg-accent/50" />
                    <span className="h-[2px] flex-1 rounded-full bg-gradient-to-r from-accent/30 via-accent/10 to-transparent" />
                  </div>

                  {/* Summary in a styled block */}
                  <div className="rounded-xl border border-accent/20 bg-accent/[0.04] p-5 sm:p-6 mb-8">
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed whitespace-pre-wrap">
                      {news[expandedIndex].summary}
                    </p>
                  </div>

                  {news[expandedIndex].galleryImages && (
                    <div className="mb-8">
                      <h3 className="text-base sm:text-lg font-heading font-bold text-foreground mb-4 block">
                        Фотогалерея
                      </h3>
                      <GalleryCarousel images={news[expandedIndex].galleryImages} label={news[expandedIndex].cardTitle} />
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className="text-base sm:text-lg font-heading font-bold text-foreground mb-4 block">
                      Ключевые моменты
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {news[expandedIndex].takeaways.map((t, j) => (
                        <span key={j} className="inline-flex items-center px-3 py-1.5 rounded-full bg-background/70 backdrop-blur-sm border border-accent/20 text-xs text-muted-foreground leading-relaxed">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-wrap justify-center gap-3">
                    {news[expandedIndex].link && (
                      <a href={news[expandedIndex].link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-accent text-accent-foreground text-sm font-body tracking-wide hover:bg-accent/90 transition-colors">
                         {news[expandedIndex].linkText || "Читать полный отчёт"}
                      </a>
                    )}
                    {news[expandedIndex].extraLinks?.map((el, idx) => (
                      <a key={idx} href={el.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-accent text-accent-foreground text-sm font-body tracking-wide hover:bg-accent/90 transition-colors">
                         {el.label}
                      </a>
                    ))}
                  </div>

                  {/* Certificate image (expandable) */}
                  {news[expandedIndex].certificateImg && (
                    <div className="mt-6">
                      <button
                        onClick={() => setCertExpanded(!certExpanded)}
                        className="group flex items-center gap-2 mb-3 hover:opacity-80 transition-opacity"
                      >
                        <Award size={16} className="text-accent" />
                        <h3 className="text-base sm:text-lg font-heading font-bold text-foreground">Сертификат</h3>
                        {certExpanded ? <ZoomOut size={14} className="text-muted-foreground" /> : <ZoomIn size={14} className="text-muted-foreground" />}
                      </button>
                      <motion.div
                        animate={{ height: certExpanded ? "auto" : "120px" }}
                        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                        className="relative overflow-hidden rounded-lg border border-accent/20 cursor-pointer"
                        onClick={() => setCertExpanded(!certExpanded)}
                      >
                        <img
                          src={news[expandedIndex].certificateImg}
                          alt="Shanghai Jiao Tong University Academic Recognition"
                          className="w-full object-cover object-top"
                        />
                        {!certExpanded && (
                          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/80 to-transparent flex items-center justify-center">
                            <span className="px-5 py-2.5 rounded-full bg-accent text-accent-foreground text-xs sm:text-sm font-heading tracking-wide shadow-lg">Нажмите для просмотра</span>
                          </div>
                        )}
                      </motion.div>
                    </div>
                  )}

                  {/* Bottom close */}
                  <div className="text-center pt-6 mt-6 border-t border-border/30">
                    <button
                      onClick={() => setExpandedIndex(null)}
                      className="text-sm font-heading tracking-wide text-muted-foreground hover:text-foreground transition-colors py-2"
                    >
                      Закрыть
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default NewsSection;

// Shared heading component — 2x size

// as: на странице должен быть ровно один h1 — его задаёт первая секция страницы,
// остальные заголовки остаются h2.
export const SectionHeading = forwardRef<
  HTMLHeadingElement,
  { children: React.ReactNode; className?: string; as?: "h1" | "h2"; underline?: boolean }
>(
  ({ children, className = "", as: Tag = "h2", underline = true }, ref) => (
    <Tag ref={ref} className={`sm:text-5xl lg:text-7xl font-heading mt-3 tracking-tight relative inline-block text-center text-3xl ${className}`}>
      {children}
      {underline && (
        <motion.span
          initial={{ width: 0 }}
          whileInView={{ width: "100%" }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="absolute -bottom-2 left-0 h-[2px] bg-gradient-to-r from-accent via-accent/50 to-transparent"
        />
      )}
    </Tag>
  )
);

SectionHeading.displayName = "SectionHeading";
